import React, { useCallback, useEffect, useState } from "react";
import {
  TrendingUp, MessageSquare, MessagesSquare, ShoppingBag, Banknote, ExternalLink,
  Users, UserPlus, Handshake, Calculator, Lightbulb, Check, X,
} from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, Toast } from "@/ui";

function Stat({ Icon, label, value, testid }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }} data-testid={testid}>
      <Icon size={16} className="text-slate-400" />
      <div>
        <div className="text-base font-semibold leading-none">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function SectionHead({ Icon, tint, title, sub }) {
  return (
    <div className="flex items-center gap-2">
      <div className="rounded-full p-2" style={{ background: tint }}><Icon size={18} /></div>
      <div>
        <div className="text-lg font-semibold">{title}</div>
        {sub ? <div className="text-sm text-slate-500">{sub}</div> : null}
      </div>
    </div>
  );
}

function WeeklyLeak({ weekly, onRespond }) {
  const s = weekly?.suggestion;
  return (
    <Card data-testid="weekly-leak-card" className="stack" style={{ gap: 14, background: "#0f172a", color: "#f8fafc" }}>
      <div className="text-xs uppercase tracking-wide" style={{ color: "var(--wash-ink-2)" }}>This week</div>
      <p data-testid="weekly-leak-sentence" className="text-lg leading-relaxed" style={{ maxWidth: 640 }}>
        {weekly?.sentence}
      </p>
      {s ? (
        <div className="rounded-xl p-4" style={{ background: "var(--accent-wash)" }}>
          <div className="flex items-start gap-2">
            <Lightbulb size={18} style={{ color: "var(--wash-ink)", flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wide" style={{ color: "var(--wash-ink-2)" }}>One thing to try</div>
              <p data-testid="suggestion-text" className="text-base" style={{ marginTop: 4 }}>{s.text}</p>
              {s.status === "open" ? (
                <div className="flex gap-2" style={{ marginTop: 12 }}>
                  <Button size="sm" variant="primary" data-testid="suggestion-approve" onClick={() => onRespond(s.id, "approved")}>
                    <Check size={15} strokeWidth={2} />I'll try it
                  </Button>
                  <Button size="sm" data-testid="suggestion-decline" onClick={() => onRespond(s.id, "declined")}>
                    <X size={15} strokeWidth={2} />Not now
                  </Button>
                </div>
              ) : (
                <Chip tone={s.status === "approved" ? "good" : "neutral"} data-testid="suggestion-status">
                  {s.status === "approved" ? "You chose to try this" : "You skipped this one"}
                </Chip>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function Funnel({ funnel }) {
  const stages = funnel?.stages || [];
  const gap = funnel?.biggestGap;
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <Card data-testid="funnel-card" className="stack" style={{ gap: 14 }}>
      <SectionHead Icon={Users} tint="var(--sunk)" title="Your funnel" sub="How many people moved through each step, and where you lose the most." />
      <div className="stack" style={{ gap: 10 }}>
        {stages.map((s) => (
          <div key={s.key} data-testid={`funnel-${s.key}`}>
            <div className="flex items-center justify-between text-sm" style={{ marginBottom: 4 }}>
              <span className="capitalize">{s.label}</span>
              <span className="num font-semibold">{s.count.toLocaleString()}</span>
            </div>
            <div style={{ height: 10, borderRadius: 999, background: "var(--sunk)", overflow: "hidden" }}>
              <div style={{ width: `${Math.max(4, (s.count / max) * 100)}%`, height: "100%", background: "var(--accent)", borderRadius: 999, transition: "width var(--t-base) var(--ease-enter)" }} />
            </div>
          </div>
        ))}
      </div>
      {gap && gap.lost > 0 ? (
        <div className="rounded-xl px-4 py-3 text-sm" style={{ background: "#fff7ed", color: "#9a3412" }} data-testid="funnel-gap">
          Biggest drop-off: <strong>{gap.clause}</strong>.
        </div>
      ) : null}
    </Card>
  );
}

export default function Numbers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/numbers/overview");
      setData(r.data);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (id, choice) => {
    try {
      await api.post(`/numbers/suggestions/${id}/respond`, { choice });
      setToast(choice === "approved" ? "Great — we'll remember you're trying this." : "Okay, skipped.");
      setTimeout(() => setToast(""), 2500);
      setData((d) => ({ ...d, weekly: { ...d.weekly, suggestion: { ...d.weekly.suggestion, status: choice } } }));
    } catch (e) { setToast(friendlyError(e)); setTimeout(() => setToast(""), 3000); }
  };

  const mpp = data?.moneyPerPost || {};
  const rows = mpp.posts || [];
  const totals = mpp.totals || {};
  const cw = data?.costToWin || {};
  const cv = data?.customerValue || {};
  const counter = data?.counter || {};

  return (
    <Layout>
      <div style={{ marginBottom: 8 }}>
        <h1 className="t-h1">Numbers</h1>
        <p className="muted">The numbers no one else can show you — traced back to what actually happened.</p>
      </div>

      {loading ? (
        <Card><Skeleton style={{ height: 220 }} /></Card>
      ) : error ? (
        <Card data-testid="numbers-error" className="text-sm text-rose-600">{error}</Card>
      ) : (
        <div className="stack" style={{ gap: 16 }}>
          {data.weekly ? <WeeklyLeak weekly={data.weekly} onRespond={respond} /> : null}

          {/* Money per post — FIRST */}
          <Card data-testid="money-per-post-card" className="stack" style={{ gap: 14 }}>
            <SectionHead Icon={TrendingUp} tint="var(--sunk)" title="Money per post" sub="Every peso, traced back to the post that earned it." />
            <div className="rounded-xl px-4 py-3" style={{ background: "#f8fafc", display: "flex", flexWrap: "wrap", gap: 24 }} data-testid="money-per-post-totals">
              <Stat Icon={Banknote} label="total earned" value={totals.money || "\u20b10.00"} testid="totals-money" />
              <Stat Icon={ShoppingBag} label="orders" value={totals.orders || 0} testid="totals-orders" />
              <Stat Icon={MessagesSquare} label="chats" value={totals.chats || 0} testid="totals-chats" />
              <Stat Icon={MessageSquare} label="comments" value={totals.comments || 0} testid="totals-comments" />
            </div>
            {rows.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-500" data-testid="money-per-post-empty">
                Link a post to a product on the Posts screen, and its money will show up here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="money-per-post-table">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-3">Post</th>
                      <th className="py-2 px-2 text-right">Comments</th>
                      <th className="py-2 px-2 text-right">Chats</th>
                      <th className="py-2 px-2 text-right">Orders</th>
                      <th className="py-2 pl-2 text-right">Money</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.postId} className="border-b last:border-0" data-testid={`mpp-row-${r.postId}`}>
                        <td className="py-3 pr-3">
                          <div className="flex items-center gap-3">
                            {r.picture ? (
                              <img src={r.picture} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }}
                                onError={(e) => { e.target.style.display = "none"; }} />
                            ) : null}
                            <div style={{ maxWidth: 320 }}>
                              <div className="line-clamp-2">{r.text || r.postId}</div>
                              <div className="text-xs text-slate-500 flex items-center gap-2">
                                {r.productName ? <span>Selling: {r.productName}</span> : null}
                                {!r.active ? <Chip tone="neutral">off</Chip> : null}
                                {r.permalink ? (
                                  <a href={r.permalink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-600">
                                    <ExternalLink size={11} /> view
                                  </a>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right">{r.comments}</td>
                        <td className="py-3 px-2 text-right">{r.chats}</td>
                        <td className="py-3 px-2 text-right font-medium">{r.orders}</td>
                        <td className="py-3 pl-2 text-right font-semibold text-emerald-700" data-testid={`mpp-money-${r.postId}`}>{r.money}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {data.funnel ? <Funnel funnel={data.funnel} /> : null}

          {/* Cost to win + customer value */}
          <div className="grid" style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            <Card data-testid="cost-to-win-card" className="stack" style={{ gap: 12 }}>
              <SectionHead Icon={UserPlus} tint="#fef2f2" title="Cost to win a customer" sub={`Over the last ${cw.windowDays || 30} days.`} />
              <div className="text-4xl font-bold num" data-testid="cost-to-win-value">{cw.costToWin || "\u20b10.00"}</div>
              <div className="text-sm text-slate-500">
                {cw.spend} spent reaching people · {cw.newCustomers} new customer{cw.newCustomers === 1 ? "" : "s"} won
              </div>
            </Card>

            <Card data-testid="customer-value-card" className="stack" style={{ gap: 12 }}>
              <SectionHead Icon={Handshake} tint="#f5f3ff" title="What a customer is worth" sub="Over time, including anyone they referred." />
              <div className="flex items-end gap-6">
                <div>
                  <div className="text-4xl font-bold num" data-testid="ltv-value">{cv.avgLtv || "\u20b10.00"}</div>
                  <div className="text-xs text-slate-500">on their own</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold num text-violet-700" data-testid="ltv-with-referrals">{cv.avgLtvWithReferrals || "\u20b10.00"}</div>
                  <div className="text-xs text-slate-500">with referrals</div>
                </div>
              </div>
              {(cv.top || []).length ? (
                <div className="stack" style={{ gap: 6, marginTop: 4 }}>
                  {cv.top.slice(0, 4).map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm" data-testid={`ltv-top-${c.id}`}>
                      <span>{c.name}{c.referredCount ? <span className="text-xs text-slate-400"> · brought {c.referredCount}</span> : null}</span>
                      <span className="num font-medium">{c.combined}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </Card>
          </div>

          {/* Counter performance */}
          <Card data-testid="counter-perf-card" className="stack" style={{ gap: 14 }}>
            <SectionHead Icon={Calculator} tint="#f0fdf4" title="At the counter" sub="Who sells most, and which suggested lines worked." />
            {(counter.leaderboard || []).length === 0 ? (
              <div className="text-sm text-slate-500 py-4">No counter sales yet in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="counter-leaderboard">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-3">Staff</th>
                      <th className="py-2 px-2 text-right">Orders</th>
                      <th className="py-2 px-2 text-right">Sold</th>
                      <th className="py-2 pl-2 text-right">Upsells taken</th>
                    </tr>
                  </thead>
                  <tbody>
                    {counter.leaderboard.map((r) => (
                      <tr key={r.staffId} className="border-b last:border-0" data-testid={`counter-staff-${r.staffId}`}>
                        <td className="py-3 pr-3 font-medium">{r.name}</td>
                        <td className="py-3 px-2 text-right">{r.orders}</td>
                        <td className="py-3 px-2 text-right font-semibold text-emerald-700">{r.money}</td>
                        <td className="py-3 pl-2 text-right">{r.took}/{r.offered} <span className="text-xs text-slate-400">({r.tookRate}%)</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(counter.lines || []).length ? (
              <div className="rounded-xl px-4 py-3" style={{ background: "#f8fafc" }} data-testid="counter-lines">
                <div className="text-xs uppercase tracking-wide text-slate-500" style={{ marginBottom: 8 }}>Lines that worked</div>
                <div className="flex flex-wrap gap-2">
                  {counter.lines.map((l) => (
                    <Chip key={l.line} tone={l.tookRate >= 40 ? "good" : "neutral"}>
                      {l.line}: {l.took}/{l.offered} ({l.tookRate}%)
                    </Chip>
                  ))}
                </div>
              </div>
            ) : null}
          </Card>
        </div>
      )}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="numbers-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
